import { useState } from 'react';
import { auth, db } from '../../services/firebase';
import { doc, setDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import AdminLayout from '../../components/admin/AdminLayout';
import { AGE_GROUPS } from '../../utils/helpers';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';
import { sendRegistrationApprovedEmail, sendRegistrationRejectedEmail, isEmailConfigured } from '../../services/emailService';

function ManageRegistrations() {
  const [user] = useAuthState(auth);
  const { club, clubId } = useClub();
  
  // Use tenant-scoped data hooks
  const { data: rawRegistrations, loading, setData: setRegistrations } = useTenantCollection('registrations');
  const { update, remove } = useTenantCrud('registrations');
  
  // Sort registrations by date (newest first)
  const registrations = [...rawRegistrations].sort((a, b) => {
    const dateA = a.submittedAt?.toDate?.() || new Date(a.submittedAt || 0);
    const dateB = b.submittedAt?.toDate?.() || new Date(b.submittedAt || 0);
    return dateB - dateA;
  });
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgeGroup, setFilterAgeGroup] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const statusOptions = ['pending', 'approved', 'rejected', 'waitlist'];
  const ageGroups = [...AGE_GROUPS, 'Tier1', 'Tier2'];

  const updateStatus = async (regId, newStatus) => {
    try {
      // Update registration status (tenant-scoped)
      await update(regId, { status: newStatus });
      
      // Find the registration for email
      const registration = rawRegistrations.find(r => r.id === regId);
      
      // Send email notification if configured
      if (isEmailConfigured() && registration) {
        if (newStatus === 'approved') {
          await sendRegistrationApprovedEmail(registration, club?.name);
        } else if (newStatus === 'rejected') {
          await sendRegistrationRejectedEmail(registration, club?.name);
        }
      }
      
      setRegistrations(rawRegistrations.map(r => 
        r.id === regId ? { ...r, status: newStatus } : r
      ));
      if (selectedRegistration?.id === regId) {
        setSelectedRegistration({ ...selectedRegistration, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteRegistration = async (regId, playerName) => {
    if (!window.confirm(`Are you sure you want to delete the registration for ${playerName}?`)) return;

    try {
      // Delete registration (tenant-scoped)
      await remove(regId);
      setRegistrations(rawRegistrations.filter(r => r.id !== regId));
      if (selectedRegistration?.id === regId) {
        setSelectedRegistration(null);
      }
    } catch (error) {
      console.error('Error deleting registration:', error);
    }
  };

  // Create player profile from approved registration
  const createPlayerFromRegistration = async (registration) => {
    if (!window.confirm(`Create a player profile for ${registration.playerFirstName} ${registration.playerLastName}?`)) return;

    try {
      // Create player document
      const playerData = {
        name: `${registration.playerFirstName} ${registration.playerLastName}`,
        position: registration.position || 'Any',
        number: 0, // Will be assigned by coach
        team: registration.ageGroup,
        age: registration.dateOfBirth ? calculateAge(registration.dateOfBirth) : 0,
        goals: 0,
        assists: 0,
        region: registration.city || '',
        email: registration.email,
        phone: registration.phone,
        parentName: `${registration.parentFirstName} ${registration.parentLastName}`,
        medicalConditions: registration.medicalConditions || '',
        allergies: registration.allergies || '',
        emergencyContact: registration.emergencyContact || '',
        registrationId: registration.id,
        createdAt: new Date()
      };

      // Add player using tenant-scoped hook
      const newPlayer = await addPlayer(playerData);

      // Try to link to user account if email matches
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', registration.email)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        await setDoc(doc(db, 'users', userDoc.id), {
          ...userDoc.data(),
          playerId: newPlayer.id,
          clubId: club?.id || clubId
        }, { merge: true });
      }

      // Update registration to mark player created
      await update(registration.id, { 
        playerCreated: true,
        playerId: newPlayer.id 
      });

      setRegistrations(rawRegistrations.map(r => 
        r.id === registration.id ? { ...r, playerCreated: true, playerId: newPlayer.id } : r
      ));

      if (selectedRegistration?.id === registration.id) {
        setSelectedRegistration({ ...selectedRegistration, playerCreated: true, playerId: newPlayer.id });
      }

      alert(`Player profile created for ${registration.playerFirstName} ${registration.playerLastName}!`);
    } catch (error) {
      console.error('Error creating player:', error);
      alert('Failed to create player profile. Please try again.');
    }
  };

  const calculateAge = (dateOfBirth) => {
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  // Get the add function for players (used in createPlayerFromRegistration)
  const { add: addPlayer } = useTenantCrud('players');

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return '#22c55e';
      case 'rejected': return '#ef4444';
      case 'waitlist': return '#f59e0b';
      default: return '#3b82f6'; // pending
    }
  };

  // Filter registrations
  const filteredRegistrations = registrations.filter(reg => {
    if (filterStatus !== 'all' && reg.status !== filterStatus) return false;
    if (filterAgeGroup !== 'all' && reg.ageGroup !== filterAgeGroup) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const playerName = `${reg.playerFirstName} ${reg.playerLastName}`.toLowerCase();
      const parentName = `${reg.parentFirstName} ${reg.parentLastName}`.toLowerCase();
      const email = (reg.email || '').toLowerCase();
      if (!playerName.includes(search) && !parentName.includes(search) && !email.includes(search)) {
        return false;
      }
    }
    return true;
  });

  // Stats
  const stats = {
    total: registrations.length,
    pending: registrations.filter(r => r.status === 'pending').length,
    approved: registrations.filter(r => r.status === 'approved').length,
    rejected: registrations.filter(r => r.status === 'rejected').length
  };

  return (
    <AdminLayout title="Player Registrations" subtitle={`Review and manage player registrations for ${club?.name || 'your club'}`}>

          {/* Stats */}
          <div className="reg-stats">
            <div className="reg-stat-card">
              <span className="stat-number">{stats.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="reg-stat-card pending">
              <span className="stat-number">{stats.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="reg-stat-card approved">
              <span className="stat-number">{stats.approved}</span>
              <span className="stat-label">Approved</span>
            </div>
            <div className="reg-stat-card rejected">
              <span className="stat-number">{stats.rejected}</span>
              <span className="stat-label">Rejected</span>
            </div>
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="filter-group">
              <label>Status:</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Age Group:</label>
              <select value={filterAgeGroup} onChange={(e) => setFilterAgeGroup(e.target.value)}>
                <option value="all">All Age Groups</option>
                {ageGroups.map(ag => (
                  <option key={ag} value={ag}>{ag}</option>
                ))}
              </select>
            </div>
            <div className="filter-group search">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Registrations List */}
          <div className="admin-card">
            <div className="card-content">
              {loading ? (
                <p className="loading-text">Loading registrations...</p>
              ) : filteredRegistrations.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📋</span>
                  <h3>No Registrations Found</h3>
                  <p>{registrations.length === 0 ? 'No registrations have been submitted yet.' : 'No registrations match your filters.'}</p>
                </div>
              ) : (
                <div className="registrations-list">
                  {filteredRegistrations.map(reg => (
                    <div 
                      key={reg.id} 
                      className={`registration-card ${selectedRegistration?.id === reg.id ? 'selected' : ''}`}
                      onClick={() => setSelectedRegistration(reg)}
                    >
                      <div 
                        className="reg-status-bar" 
                        style={{ backgroundColor: getStatusColor(reg.status) }}
                      ></div>
                      <div className="reg-main-info">
                        <h3>{reg.playerFirstName} {reg.playerLastName}</h3>
                        <div className="reg-meta">
                          <span className="reg-age-group">{reg.ageGroup}</span>
                          <span className="reg-position">{reg.position || 'Any'}</span>
                          <span className="reg-date">{formatDate(reg.submittedAt)}</span>
                        </div>
                      </div>
                      <div className="reg-contact">
                        <span>{reg.email}</span>
                        <span>{reg.phone}</span>
                      </div>
                      <div className="reg-status">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(reg.status) }}
                        >
                          {reg.status || 'pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

      {/* Registration Detail Modal */}
      {selectedRegistration && (
        <div className="modal-overlay" onClick={() => setSelectedRegistration(null)}>
          <div className="modal-content modal-xlarge" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registration Details</h2>
              <button className="modal-close" onClick={() => setSelectedRegistration(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Status Controls */}
              <div className="reg-detail-status">
                <span className="current-status">
                  Status: 
                  <span 
                    className="status-badge large"
                    style={{ backgroundColor: getStatusColor(selectedRegistration.status) }}
                  >
                    {selectedRegistration.status || 'pending'}
                  </span>
                </span>
                <div className="status-actions">
                  <button 
                    className="btn-status approve"
                    onClick={() => updateStatus(selectedRegistration.id, 'approved')}
                    disabled={selectedRegistration.status === 'approved'}
                  >
                    Approve
                  </button>
                  <button 
                    className="btn-status waitlist"
                    onClick={() => updateStatus(selectedRegistration.id, 'waitlist')}
                    disabled={selectedRegistration.status === 'waitlist'}
                  >
                    Waitlist
                  </button>
                  <button 
                    className="btn-status reject"
                    onClick={() => updateStatus(selectedRegistration.id, 'rejected')}
                    disabled={selectedRegistration.status === 'rejected'}
                  >
                    Reject
                  </button>
                </div>
              </div>

              {/* Player Info */}
              <div className="reg-detail-section">
                <h3>Player Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <span>{selectedRegistration.playerFirstName} {selectedRegistration.playerLastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date of Birth</label>
                    <span>{selectedRegistration.dateOfBirth}</span>
                  </div>
                  <div className="detail-item">
                    <label>Gender</label>
                    <span>{selectedRegistration.gender}</span>
                  </div>
                  <div className="detail-item">
                    <label>Age Group</label>
                    <span>{selectedRegistration.ageGroup}</span>
                  </div>
                  <div className="detail-item">
                    <label>Position</label>
                    <span>{selectedRegistration.position || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Parent Info */}
              <div className="reg-detail-section">
                <h3>Parent/Guardian Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <span>{selectedRegistration.parentFirstName} {selectedRegistration.parentLastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <span><a href={`mailto:${selectedRegistration.email}`}>{selectedRegistration.email}</a></span>
                  </div>
                  <div className="detail-item">
                    <label>Phone</label>
                    <span><a href={`tel:${selectedRegistration.phone}`}>{selectedRegistration.phone}</a></span>
                  </div>
                  <div className="detail-item">
                    <label>Address</label>
                    <span>{selectedRegistration.address}</span>
                  </div>
                  <div className="detail-item">
                    <label>City</label>
                    <span>{selectedRegistration.city}</span>
                  </div>
                </div>
              </div>

              {/* Medical Info */}
              <div className="reg-detail-section">
                <h3>Medical Information</h3>
                <div className="detail-grid">
                  <div className="detail-item full">
                    <label>Medical Conditions</label>
                    <span>{selectedRegistration.medicalConditions || 'None specified'}</span>
                  </div>
                  <div className="detail-item full">
                    <label>Allergies</label>
                    <span>{selectedRegistration.allergies || 'None specified'}</span>
                  </div>
                  <div className="detail-item full">
                    <label>Prior Injuries</label>
                    <span>{selectedRegistration.priorInjuries || 'None specified'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Emergency Contact</label>
                    <span>{selectedRegistration.emergencyContact}</span>
                  </div>
                  <div className="detail-item">
                    <label>Emergency Phone</label>
                    <span>{selectedRegistration.emergencyPhone}</span>
                  </div>
                </div>
              </div>

              {/* Submission Info */}
              <div className="reg-detail-section">
                <h3>Submission Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Submitted</label>
                    <span>{formatDate(selectedRegistration.submittedAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Photo Consent</label>
                    <span>{selectedRegistration.agreePhotos ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-delete"
                onClick={() => deleteRegistration(selectedRegistration.id, `${selectedRegistration.playerFirstName} ${selectedRegistration.playerLastName}`)}
              >
                Delete Registration
              </button>
              {selectedRegistration.status === 'approved' && !selectedRegistration.playerCreated && (
                <button 
                  className="btn-save"
                  onClick={() => createPlayerFromRegistration(selectedRegistration)}
                >
                  Create Player Profile
                </button>
              )}
              {selectedRegistration.playerCreated && (
                <span className="player-created-badge">Player Profile Created</span>
              )}
              <button className="btn-cancel" onClick={() => setSelectedRegistration(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageRegistrations;
